<template>
  <div
    v-tooltip="tooltip"
    class="h-full group"
    @click="expandCard"
    @mouseover="onMouseOver"
    @mouseout="onMouseOut"
  >
    <DropZoneFileUpload
      class="w-full h-full"
      :accepted-file-types="['application/pdf', 'pdf']"
      @file-dropped="uploadStudyPdfFile"
    >
      <template #default="{ triggerFilePicker }">
        <div
          ref="expandablePanelElement"
          class="absolute left-0 scroll-anchor"
          style="top: -100px"
        />
        <div
          class="relative h-full transition-colors duration-1000 ease-in-out"
          :class="{
            'bg-neutral-background-secondary': isHoveredOver,
            duplicate: reviewItem.parentStudyId,
            'maybe-dupllicate': reviewItem.maybeParentStudyId,
            'duplication-parent': isParentStudy,
            quarantined: showQuarantined && reviewItem.state === StudyState.QUARANTINED,
          }"
        >
          <div class="p-5 flex flex-col shrink-0 sticky top-20 left-0">
            <ExpansionPanel
              :model-value="expansionPanel.isShown.value"
              disable-close-animation
              disable-open-animation
            >
              <template #activator>
                <div class="space-y-2 w-full">
                  <div class="inline-flex items-start justify-between w-full gap-2 min-w-0">
                    <div class="flex gap-2 items-start">
                      <div class="flex flex-col items-center gap-2">
                        <StudyFavoriteButton
                          :study="reviewItem"
                          :is-readonly="review.isReviewReadonly.value"
                          @toggle-favorite="toggleFavorite"
                        />
                        <FullViewButton
                          :review-item="reviewItem"
                          :pdf-price="pdfPrice"
                          :is-pdf-price-loading="isPdfPriceLoading"
                          :is-create-article-pdf-loading="isCreateArticlePdfLoading"
                          :is-mira-integration-enabled="isMiraIntegrationEnabled"
                          @trigger-upload="triggerFilePicker"
                          @fetch-pdf-price="getArticlePdfPrice"
                          @create-article-pdf-order="createArticlePdfOrder"
                        />
                      </div>
                      <div class="w-full min-w-0 space-y-2.5">
                        <div
                          class="flex justify-between gap-2 w-full sticky bottom-0 right-0 text-base"
                        >
                          <slot name="title">
                            <p
                              v-if="reviewItem.metadata?.title"
                              class="font-medium text-neutral-text-title"
                              :class="{
                                'text-primary-text-default': expansionPanel.isShown.value,
                                'line-clamp-2': !expansionPanel.isShown.value,
                              }"
                              v-html="formattedTitle"
                            />
                            <p v-else class="text-sm text-gray-400 w-full">Title not available</p>
                          </slot>
                        </div>
                        <slot name="subtitle" />
                      </div>
                    </div>

                    <FloatingMenu v-if="!review.isReviewReadonly.value">
                      <template #activator="{ show }">
                        <button aria-label="Review item actions" class="w-fit" @click.stop="show">
                          <EllipsisVerticalIcon class="w-5 h-auto" />
                        </button>
                      </template>
                      <template #content>
                        <div>
                          <slot name="menu-actions" />
                          <SubmitComplaint :study="reviewItem" />
                        </div>
                      </template>
                    </FloatingMenu>
                  </div>
                </div>
              </template>
              <template #default>
                <div class="w-full py-4 space-y-2">
                  <div class="flex items-center justify-between gap-2">
                    <slot name="expanded-header">
                      <a
                        class="underline flex items-center"
                        :href="itemUrl"
                        target="_blank"
                        @click.stop
                      >
                        <SourceIcon :review-item="reviewItem" />
                      </a>
                    </slot>

                    <div class="text-primary-text-default flex items-center">
                      id:
                      <p v-html="formattedId" />
                    </div>
                  </div>

                  <slot name="expanded-content" />
                </div>
              </template>
            </ExpansionPanel>
          </div>
        </div>
      </template>
      <template #filePresentInWindow="{ hasFileDraggedOver }">
        <div
          class="w-full h-full flex items-center justify-center rounded-lg z-50 border-2 border-dashed transition-colors"
          :class="
            hasFileDraggedOver
              ? 'border-primary-500 bg-primary-100/80'
              : 'border-b-primary-300 border-transparent bg-white/60'
          "
        >
          <ArrowDownTrayIcon
            class="w-16 h-16 transition-colors"
            :class="hasFileDraggedOver ? 'text-primary-500/60' : 'text-primary-300/60'"
          />
        </div>
      </template>
    </DropZoneFileUpload>
  </div>
</template>

<script setup lang="ts">
import { ref, toRef } from "vue";
import FloatingMenu from "@app/components/Global/FloatingMenu.vue";
import DropZoneFileUpload from "@app/components/Global/Inputs/DropZoneFileUpload.vue";
import ExpansionPanel from "@app/components/Global/ExpansionPanel.vue";
import { EllipsisVerticalIcon, ArrowDownTrayIcon } from "@heroicons/vue/24/outline";
import SubmitComplaint from "./components/SubmitComplaint.vue";
import StudyFavoriteButton from "./components/StudyFavoriteButton.vue";
import FullViewButton from "./components/FullViewButton.vue";
import SourceIcon from "./components/SourceIcon.vue";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { StudyState } from "@app/types";
import useExpansionPanel from "@app/composables/use-expansion-panel";
import { useReviewItemDetails } from "./use-review-item-details";

const props = withDefaults(
  defineProps<{
    reviewItem: ReviewItem;
    expansionPanel: ReturnType<typeof useExpansionPanel>;
    tooltip?: string;
    itemUrl?: string;
    showQuarantined?: boolean;
  }>(),
  {
    tooltip: "",
    itemUrl: "",
    showQuarantined: true,
  },
);

const expandablePanelElement = ref<HTMLElement>();

const {
  review,
  isHoveredOver,
  isParentStudy,
  formattedId,
  formattedTitle,
  pdfPrice,
  isPdfPriceLoading,
  isCreateArticlePdfLoading,
  isMiraIntegrationEnabled,
  toggleFavorite,
  uploadStudyPdfFile,
  getArticlePdfPrice,
  createArticlePdfOrder,
  expandCard,
  onMouseOver,
  onMouseOut,
} = useReviewItemDetails(toRef(props, "reviewItem"), props.expansionPanel, expandablePanelElement);
</script>
